import { hasOwnProperty } from "@glideapps/ts-necessities";
import { GlideDateTime } from "../../date-time/glide-date-time";
import { withTimeout } from "../../support/timeout";
import { actionRunTimeout } from "../action-timeout";
import { logError } from "../debug-print";
import type { JSONData, JSONObject } from "../Support";
import { assert, checkArray, panic } from "../Support";
import {
    asObjectValue,
    ForwardingValueProducer,
    isLoadedDefined,
    isValueNotEmpty,
    nonLoading,
    RecomputingFromObjectValueProducer,
    SubscribableValueProducer,
    waitForObjectInspectorFromValueProducer,
    waitForValueFromValueProducer,
} from "./component-helpers";
import type {
    Context,
    DeserializableKind,
    DeserializationSpec,
    EnvironmentBoundEvaluable,
    EvalEnvironment,
    ForceValueProducer,
    Loading,
    ObjectInspector,
    ObjectPropertyGetter,
    ObjectValue,
    PotentialValue,
    PrimitiveValue,
    Value,
    ValueProducer,
} from "./computation-types";
import {
    checkObjectValue,
    isObjectValue,
    isPrimitiveValue,
    isValueAllowedInAction,
    LOADING,
    UnsubscribableValueProducer,
} from "./computation-types";

function ensureUnique<O, A>(v: Value<O, A>): Value<O, A> {
    if (Array.isArray(v)) {
        return v.slice();
    }
    if (v instanceof GlideDateTime) {
        return v.clone();
    }
    if (isObjectValue(v)) {
        return { ...v };
    }
    return v;
}

//O: AppOther, a type a value can be
//A: appEnvironment
export class Data<O, A>
    extends SubscribableValueProducer<O, A>
    implements EnvironmentBoundEvaluable<O, A>, ObjectInspector<O, A>
{
    constructor(private _current: Value<O, A>) {
        super();
    }

    public get current(): Value<O, A> {
        return this._current;
    }

    public setListenersDirty(): void {
        super.setListenersDirty();
    }

    // FIXME: This and all the `...WithoutUpdate` methods are a hack that allows
    // us to do `setDataDeep` with sending updates to each `Data` at most once.
    // Implementing proper dirtying and forcing will make this unnecessary.
    public setWithoutUpdate(v: Value<O, A>): boolean {
        if (this._current === v) return false;
        this._current = ensureUnique(v);
        return true;
    }

    public set(v: Value<O, A>): boolean {
        if (this.setWithoutUpdate(v)) {
            this.setListenersDirty();
            return true;
        }
        return false;
    }

    public addPropertyWithoutUpdate(name: string, vp: ValueProducer<O, A>): void {
        assert(isObjectValue(this._current));
        if (hasOwnProperty(this._current, name)) {
            logError(`Property ${name} already defined in data`);
        }

        (this._current as any)[name] = vp;
    }

    public addProperty(name: string, vp: ValueProducer<O, A>): void {
        this.addPropertyWithoutUpdate(name, vp);
        this.setListenersDirty();
    }

    public removePropertyWithoutUpdate(name: string): boolean {
        assert(isObjectValue(this._current));
        if (!hasOwnProperty(this._current, name)) {
            return false;
        }

        delete (this._current as any)[name];
        return true;
    }

    public removeProperty(name: string): void {
        if (this.removePropertyWithoutUpdate(name)) {
            this.setListenersDirty();
        }
    }

    public appendItemWithoutUpdate(vp: ValueProducer<O, A>): void {
        if (!Array.isArray(this._current)) {
            return panic("Tried to append item to a non-array");
        }
        this._current.push(vp);
    }

    public appendItem(vp: ValueProducer<O, A>): void {
        this.appendItemWithoutUpdate(vp);
        this.setListenersDirty();
    }

    public removeLastItemWithoutUpdate(): ValueProducer<O, A> | undefined {
        if (!Array.isArray(this._current)) {
            return panic("Tried to remove item from a non-array");
        }
        if (this._current.length === 0) {
            return panic("Cannot remove item from an empty array");
        }
        return this._current.pop();
    }

    public removeLastItem(): void {
        this.removeLastItemWithoutUpdate();
        this.setListenersDirty();
    }

    public removeItem(item: ValueProducer<O, A>): void {
        if (!Array.isArray(this._current)) {
            return panic("Tried to remove item from a non-array");
        }
        const newArray = this._current.filter(i => i !== item);
        if (newArray.length === this._current.length) return;
        this._current = newArray;
        this.setListenersDirty();
    }

    public removeFromParent(): void {
        return panic("Cannot remove Data from parent");
    }

    public removeItemAtIndex(index: number): void {
        if (!Array.isArray(this._current)) {
            return panic("Tried to remove item from a non-array");
        }
        if (index < 0 || this._current.length <= index) {
            return panic("Index of item to remove is out of bounds");
        }
        this._current.splice(index, 1);
        this.setListenersDirty();
    }

    public eval(_context: Context<O, A>): ValueProducer<O, A> {
        return this;
    }

    public evalInEnvironment(_env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return this;
    }

    public getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined {
        const current = force !== undefined ? force(this) : this._current;
        if (!isObjectValue(current)) return undefined;
        return this;
    }

    public getProperty(name: string): ValueProducer<O, A> | undefined {
        const current = this._current as ObjectValue<O, A>;
        // FIXME: Remove once sufficiently tested
        assert(isObjectValue(current));
        return current[name];
    }

    public getObjectValue(): ObjectValue<O, A> {
        return asObjectValue(this._current);
    }

    public static fromJSON<OInner, AInner>(json: unknown): Data<OInner, AInner> {
        return new Data(makeValueFromJSON(json, false));
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Data", "evaluable", ["data", "json"]];
    }
}

export interface ComputedColumnProvider<O, A> {
    makeComputedColumn(row: RowData<O, A>, name: string): ValueProducer<O, A> | undefined;
    getAllComputedColumnNames(): readonly string[] | undefined;
}

export class TableData<O, A> extends Data<O, A> {
    constructor(
        current: Value<O, A>,
        public readonly computedColumnProvider: ComputedColumnProvider<O, A> | undefined
    ) {
        super(current);
    }
}

export class RowData<O, A> extends Data<O, A> {
    constructor(current: Value<O, A>, private readonly _table: TableData<O, A>) {
        super(current);
        assert(isObjectValue(current));
    }

    public setWithoutUpdate(_v: Value<O, A>): boolean {
        return panic("Cannot set a RowData");
    }

    public removePropertyWithoutUpdate(name: string): boolean {
        const { current } = this;
        assert(isObjectValue(current));
        if (!hasOwnProperty(current, name)) return false;
        const vp = current[name];
        // In `getPropertyValueProducer` we give out `Data` row properties
        // directly, without a `GetPropertyValueProducer`, so we can't just
        // yank them out here, since somebody might depend on updates from
        // them.  Instead, we just set them to `undefined`.  With computed
        // columns we can't do that.
        if (vp instanceof Data) {
            vp.set(undefined);
        } else {
            delete current[name];
        }
        return false;
    }

    public removeFromParent(): void {
        const parent = this._table;
        const parentArray = parent.current;
        if (!Array.isArray(parentArray)) {
            return panic("Tried to remove item from a non-array");
        }
        const index = parentArray.indexOf(this);
        if (index < 0) {
            return panic("Array element to be removed not found");
        }
        parentArray.splice(index, 1);
        parent.setListenersDirty();
    }

    public getProperty(name: string): ValueProducer<O, A> | undefined {
        const { current } = this;
        assert(isObjectValue(current));
        let vp: ValueProducer<O, A> | undefined = current[name];
        if (vp === undefined) {
            vp = this._table.computedColumnProvider?.makeComputedColumn(this, name);
            if (vp !== undefined) {
                current[name] = vp;
                // I'm not sure whether we have to call `this.setListenersDirty()` here.
                // `getPropertyValueProducer` should always go through this code
                // path, so we should never end up in a situation where we haven't
                // made this computed column despite somebody waiting for it.  It
                // could be that somebody was waiting for it since a time when the
                // provider didn't know it existed yet?
            }
        }
        return vp;
    }

    public getObjectValue(): ObjectValue<O, A> {
        for (const columnName of this._table.computedColumnProvider?.getAllComputedColumnNames() ?? []) {
            this.getProperty(columnName);
        }
        const { current } = this;
        assert(isObjectValue(current));
        return current;
    }
}

export class Constant<O, A>
    extends UnsubscribableValueProducer<O, A>
    implements EnvironmentBoundEvaluable<O, A>, ObjectInspector<O, A>
{
    constructor(public readonly current: Value<O, A>) {
        super();
    }

    public eval(_context: Context<O, A>): ValueProducer<O, A> {
        return this;
    }

    public evalInEnvironment(_env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return this;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        if (!isObjectValue(this.current)) return undefined;
        return this;
    }

    public getProperty(name: string): ValueProducer<O, A> | undefined {
        const current = this.current as ObjectValue<O, A>;
        // FIXME: Remove once sufficiently tested
        assert(isObjectValue(current));
        return current[name];
    }

    public getObjectValue(): ObjectValue<O, A> {
        return asObjectValue(this.current);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Constant", "evaluable", ["data", "constant-json"]];
    }
}

export enum RefPredicate {
    KeyContainsValue = "key-contains-value",
}

type RefKey = string | ReadonlyArray<string>;

export function refKeyComponents(key: RefKey): ReadonlyArray<string> {
    return typeof key === "string" ? key.split("/") : key;
}

export interface KeyRefObject {
    readonly $ref: string;
    readonly $key: RefKey;
    readonly $value?: unknown;
    readonly $multiple: boolean;
    readonly $predicate?: RefPredicate;
}

export interface ArrayRefObject {
    readonly $ref: ".";
    readonly $arrayItems: ReadonlyArray<string>;
}

export function makeArrayRefObject(itemColumnNames: readonly string[]): ArrayRefObject {
    return {
        $ref: ".",
        $arrayItems: itemColumnNames,
    };
}

export type RefObject = KeyRefObject | ArrayRefObject;

export function isRefObject(obj: unknown): obj is RefObject {
    return typeof obj === "object" && hasOwnProperty(obj, "$ref") && typeof obj.$ref === "string";
}

export function isKeyRefObject(obj: unknown): obj is KeyRefObject {
    return isRefObject(obj) && hasOwnProperty(obj, "$key");
}

export function isArrayRefObject(obj: unknown): obj is ArrayRefObject {
    return isRefObject(obj) && !isKeyRefObject(obj);
}

export function getRefObjectTable({ $ref }: RefObject): string | undefined {
    if (!$ref.startsWith("#/")) {
        logError(`$ref doesn't start with '#/': ${$ref}`);
        return undefined;
    }
    return $ref.substring(2);
}

export function getStringOrStringArrayColumn(
    parentObject: JSONObject,
    columnName: string,
    isPartial: boolean
): readonly string[] | undefined {
    const columnValue = parentObject[columnName];
    if (isArrayRefObject(columnValue)) {
        return resolveArrayRef(columnValue.$arrayItems, parentObject, undefined).filter(
            v => typeof v === "string"
        ) as string[];
    } else if (typeof columnValue === "string") {
        return [columnValue];
    } else if (columnValue === undefined && isPartial) {
        return undefined;
    } else {
        return [];
    }
}

function equalityCheckWithGlideDateTimes(l: unknown, r: unknown): boolean {
    if (l instanceof GlideDateTime) {
        return l.compareTo(r) === 0;
    } else if (r instanceof GlideDateTime) {
        return r.compareTo(l) === 0;
    } else {
        return l === r;
    }
}

export function isRefMatch<O, A>(
    key: Value<O, A>,
    parentOfKey: ObjectValue<O, A> | undefined,
    { $value: value, $predicate: predicate }: KeyRefObject
): boolean {
    if (isKeyRefObject(key)) {
        return equalityCheckWithGlideDateTimes(key.$value, value);
    }
    if (predicate === RefPredicate.KeyContainsValue) {
        if (isRefObject(key)) {
            if (isKeyRefObject(key)) return false;
            if (parentOfKey === undefined) return false;
            key = resolveArrayRef(key.$arrayItems, parentOfKey, undefined);
        }
        if (Array.isArray(key)) {
            return key.find(vp => nonLoading(vp.current) === value) !== undefined;
        }
        // If the key is not an array we fall through and compare as if
        // it were an array of one.
    }
    return equalityCheckWithGlideDateTimes(key, value);
}

type ValueSetter<O, A> = (v: Value<O, A>) => void;
type ValueProducerSetter<O, A> = (v: ValueProducer<O, A>) => void;

interface RefSetter<O, A> extends KeyRefObject {
    readonly setter: ValueSetter<O, A>;
    readonly root: Value<O, A>;
}

// returns [data, parentObjectOfData]
function followValue<O, A>(root: Value<O, A>, ref: RefKey): [Value<O, A>, ObjectValue<O, A> | undefined] | null {
    const parts = refKeyComponents(ref);
    assert(root !== LOADING);
    if (root === undefined) {
        logError(`Root of loaded data is undefined`);
        return null;
    }
    let data: Value<O, A> = root;
    let parent: ObjectValue<O, A> | undefined;
    for (const part of parts) {
        let valueProducer: ValueProducer<O, A> | undefined;
        if (Array.isArray(data)) {
            if (part.match("^[0-9]+$") === null) {
                return null;
            }
            valueProducer = data[parseInt(part, 10)];
            parent = undefined;
        } else {
            if (!hasOwnProperty(data, part)) {
                return null;
            }
            parent = data as ObjectValue<O, A>;
            valueProducer = parent[part];
        }

        if (valueProducer === undefined) {
            return null;
        }

        data = nonLoading(valueProducer.current);
    }

    return [data, parent];
}

export function resolveArrayRef<T>(
    arrayItems: readonly string[],
    parentObject: Record<string, T> | undefined,
    parentObjectGetter: ObjectPropertyGetter<T> | undefined
): T[] {
    const values: T[] = [];
    for (const key of arrayItems) {
        const v = parentObject?.[key] ?? parentObjectGetter?.getProperty(key);
        if (v !== undefined && isValueNotEmpty(v) === true) {
            values.push(v);
        }
    }
    return values;
}

export class ArrayRefValueProducer<O, A> extends RecomputingFromObjectValueProducer<O, A> {
    constructor(private readonly _arrayItems: readonly string[], parent: ValueProducer<O, A>) {
        super(undefined, parent);
    }

    protected recomputeFromObject(inspector: ObjectInspector<O, A>): PotentialValue<O, A> {
        return resolveArrayRef(this._arrayItems, undefined, inspector);
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

function resolveRef<O, A>(refSetter: RefSetter<O, A>, strict: boolean): void {
    const { $ref: ref, setter, root, $key: key, $value: value, $multiple: multiple } = refSetter;

    const tableName = getRefObjectTable(refSetter);
    assert(tableName !== undefined);

    const dataWithParent = followValue(root, tableName);
    assert(dataWithParent !== null);
    let [data] = dataWithParent;

    if (key !== undefined) {
        if (!Array.isArray(data)) {
            return panic(`Trying to lookup ${key} = ${value} in a non-array at ${ref}`);
        }
        const matches = data.filter(vp => {
            // If we can't follow the reference, it's not a match.  As long as we're
            // strict, we treat this as not resolving, but once the strict pass is
            // done, we just get as many matches as we can.
            const followedWithParent = followValue(nonLoading(vp.current), key);
            if (followedWithParent !== null) {
                const [followed, parent] = followedWithParent;
                return isRefMatch(followed, parent, refSetter);
            } else {
                assert(!strict);
                return false;
            }
        });
        if (multiple) {
            data = matches;
        } else {
            if (matches.length === 0) {
                return panic(`Could not find target for ${key} = ${value} at ${ref}`);
            }
            data = nonLoading(matches[0].current);
        }
    }

    setter(data);
}

export function makeValueFromJSON<O, A>(root: JSONData, constant: boolean): Value<O, A> {
    const newContainer = (v: Value<O, A>): ValueProducer<O, A> =>
        constant ? new Constant<O, A>(v) : new Data<O, A>(v);

    const allRefSetters: RefSetter<O, A>[] = [];

    // For `null`, we never call the setter - `null` values are treated like
    // `undefined` in JavaScript, i.e., missing.  We don't add them to arrays,
    // and we don't set them in objects.
    function setValue(
        json: JSONData,
        currentRoot: Value<O, A>,
        parentObject: ValueProducer<O, A> | undefined,
        setter: ValueProducerSetter<O, A>
    ): void {
        if (json === undefined) {
            return panic("Value is undefined");
        }
        if (isPrimitiveValue(json)) {
            return setter(newContainer(json));
        }
        if (Array.isArray(json)) {
            const arr: ValueProducer<O, A>[] = [];
            if (currentRoot === undefined) {
                currentRoot = arr;
            }
            for (let i = 0; i < json.length; i++) {
                const value = json[i];
                if (value === undefined) continue;
                setValue(value, currentRoot, undefined, vp => arr.push(vp));
            }
            return setter(newContainer(arr));
        }
        if (typeof json !== "object") {
            return panic("Value is not an object");
        }
        if (json === null) {
            return;
        }
        if (isRefObject(json)) {
            if (isKeyRefObject(json)) {
                const refObject = json;
                const refSetter: RefSetter<O, A> = {
                    ...refObject,
                    setter: v => setter(newContainer(v)),
                    root: currentRoot,
                };
                allRefSetters.push(refSetter);
            } else {
                if (parentObject === undefined) {
                    return setter(newContainer([]));
                } else {
                    return setter(new ArrayRefValueProducer(json.$arrayItems, parentObject));
                }
            }
            return;
        }
        const obj: ObjectValue<O, A> = {};
        const objVP = newContainer(obj);
        if (currentRoot === undefined) {
            currentRoot = obj;
        }
        // First set all the non-refs, then the refs, so the array refs
        // find all their items.
        const arrayRefs: [string, ArrayRefObject][] = [];
        for (const key of Object.keys(json)) {
            const value = (json as any)[key];
            if (value === undefined) {
                continue;
            }
            if (isRefObject(value) && !isKeyRefObject(value)) {
                arrayRefs.push([key, value]);
                continue;
            }
            setValue(value, currentRoot, objVP, vp => (obj[key] = vp));
        }
        for (const [key, arrayRef] of arrayRefs) {
            setValue(arrayRef, currentRoot, objVP, vp => (obj[key] = vp));
        }
        return setter(objVP);
    }

    function resolvePass(refSetters: ReadonlyArray<RefSetter<O, A>>, strict: boolean): void {
        for (;;) {
            const newRefSetters: RefSetter<O, A>[] = [];
            let didSet = false;
            let iterations = 0;

            for (const rs of refSetters) {
                try {
                    resolveRef(rs, strict);
                    didSet = true;
                } catch {
                    if (iterations++ > 100000) {
                        logError("Over 100,000 iterations in makeValueFromJSON.resolvePass");
                        iterations = 0;
                    }
                    newRefSetters.push(rs);
                }
            }

            if (newRefSetters.length === 0) break;
            if (!didSet) {
                logError("Could not resolve references", newRefSetters);
                break;
            }

            refSetters = newRefSetters;
        }
    }

    if (root === undefined) {
        return panic("App root is undefined");
    }

    let resultRoot: Value<O, A>;
    setValue(root, undefined, undefined, vp => (resultRoot = nonLoading(vp.current)));

    // FIXME: I don't think `strict` makes any sense at all.  The way we should
    // resolve refs is to do as many passes as it takes until nothing changes
    // anymore.
    resolvePass(allRefSetters, false);
    resolvePass(allRefSetters, false);
    resolvePass(allRefSetters, true);

    return resultRoot;
}

export function setDataDeep<O, A>(
    destination: ValueProducer<O, A>,
    source: ValueProducer<O, A>,
    shouldRecur?: (v: ObjectValue<O, A>) => boolean
): void {
    // The data might be cyclical, so we have to make sure we don't recur
    // infinitely.  We add to `sourceSeen` each `ValueProducer` in `source`
    // that's an array or object we recur into.
    const sourceSeen = new Set<ValueProducer<O, A>>();
    const toUpdate = new Set<Data<O, A>>();

    function set(dst: ValueProducer<O, A>, src: ValueProducer<O, A>): void {
        if (dst instanceof ForwardingValueProducer) {
            const forwardee = dst.getCurrentForwardee();
            if (forwardee !== LOADING && forwardee !== undefined) {
                dst = forwardee;
            }
        }

        // FIXME: What should actually happen here if we've already seen
        // the destination, but not the source, is to create a new value
        // for the destination.  And even then, the case that the same source
        // object could occur multiple times is not guarded against, so we
        // might need a map from source to destination objects.
        if (sourceSeen.has(src)) return;

        const srcValue = src.current;
        let dstValue = dst.current;
        // FIXME: If the source is loading, we should subscribe to it, and do the
        // update once it's loaded, then unsubscribe immediately.
        if (srcValue === LOADING) {
            return panic("Can't deal with LOADING values in setDataDeep yet");
        }
        if (Array.isArray(srcValue)) {
            if (!Array.isArray(dstValue)) {
                if (dst instanceof Data) {
                    if (dst.setWithoutUpdate(srcValue)) {
                        toUpdate.add(dst);
                    }
                } else {
                    dst.set(srcValue);
                }
                return;
            }

            sourceSeen.add(src);

            const len = srcValue.length;
            for (let i = 0; i < len; i++) {
                const srcItem = srcValue[i];
                if (dstValue.length <= i) {
                    if (dst instanceof Data) {
                        dst.appendItemWithoutUpdate(srcItem);
                        toUpdate.add(dst);
                    } else {
                        dst.appendItem(srcItem);
                    }
                    dstValue = checkArray(dst.current);
                } else {
                    set(dstValue[i], srcItem);
                }
            }
            while (dstValue.length > len) {
                if (dst instanceof Data) {
                    dst.removeLastItemWithoutUpdate();
                    toUpdate.add(dst);
                } else {
                    dst.removeLastItem();
                }
                dstValue = checkArray(dst.current);
            }
        } else if (isObjectValue(srcValue)) {
            if (
                !isObjectValue(dstValue) ||
                (shouldRecur !== undefined && (!shouldRecur(dstValue) || !shouldRecur(srcValue)))
            ) {
                if (dst instanceof Data) {
                    if (dst.setWithoutUpdate(srcValue)) {
                        toUpdate.add(dst);
                    }
                } else {
                    dst.set(srcValue);
                }
                return;
            }

            sourceSeen.add(src);

            for (const p of Object.keys(dstValue)) {
                if (srcValue[p] === undefined) {
                    if (dst instanceof Data) {
                        if (dst.removePropertyWithoutUpdate(p)) {
                            toUpdate.add(dst);
                        }
                    } else {
                        dst.removeProperty(p);
                    }
                    dstValue = checkObjectValue(dst.current);
                }
            }
            for (const p of Object.keys(srcValue)) {
                const srcItem = srcValue[p];
                const dstItem = dstValue[p];
                if (dstItem === undefined) {
                    if (dst instanceof Data) {
                        dst.addPropertyWithoutUpdate(p, srcItem);
                        toUpdate.add(dst);
                    } else {
                        dst.addProperty(p, srcItem);
                    }
                    dstValue = checkObjectValue(dst.current);
                } else {
                    set(dstItem, srcItem);
                }
            }
        } else {
            assert(typeof dst.set === "function");
            if (dst instanceof Data) {
                if (dst.setWithoutUpdate(srcValue)) {
                    toUpdate.add(dst);
                }
            } else {
                dst.set(srcValue);
            }
        }
    }

    set(destination, source);

    for (const d of toUpdate) {
        d.setListenersDirty();
    }
}

export interface ValuesAndDatas<O, A> {
    values: Record<string, PrimitiveValue>;
    datas: Record<string, Data<O, A>>;
}

export async function waitForValueWithTimeout<O, A>(
    vp: ValueProducer<O, A>,
    timeout: number
): Promise<PotentialValue<O, A>> {
    return await withTimeout(waitForValueFromValueProducer(vp), LOADING, undefined, timeout);
}

async function extractActionColumnValues<O, A>(
    columnValuesVP: ValueProducer<O, A>,
    columnNames: ReadonlySet<string> | undefined
): Promise<ValuesAndDatas<O, A> | undefined> {
    if (columnValuesVP instanceof ForwardingValueProducer) {
        const forwardee = columnValuesVP.getCurrentForwardee();
        if (isLoadedDefined(forwardee)) {
            columnValuesVP = forwardee;
        }
    }

    const columnsInspector = await waitForObjectInspectorFromValueProducer(columnValuesVP);
    if (columnsInspector === undefined) return undefined;
    const columnValuesObject = columnsInspector.getObjectValue();

    const values: { [column: string]: PrimitiveValue } = {};
    const datas: { [column: string]: Data<O, A> } = {};

    await Promise.all(
        Object.keys(columnValuesObject).map(async c => {
            if (columnNames !== undefined && !columnNames.has(c)) return;

            const v = await waitForValueFromValueProducer(columnValuesObject[c]);
            if (v === LOADING) return;

            if (!isPrimitiveValue(v) || !isValueAllowedInAction(v)) {
                logError("Trying to add a non-primitive value to a table", v);
                return;
            }

            values[c] = v ?? "";
            datas[c] = new Data<O, A>(v);
        })
    );

    return { values, datas };
}

export async function extractActionColumnValuesFromValueProducer<O, A>(
    columnValuesVP: ValueProducer<O, A>,
    columnNames: ReadonlySet<string> | undefined
): Promise<ValuesAndDatas<O, A> | undefined> {
    return await withTimeout(
        extractActionColumnValues(columnValuesVP, columnNames),
        undefined,
        undefined,
        actionRunTimeout
    );
}
